import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, ArrowLeft } from "lucide-react";

/**
 * Event Settings — admin-only editor for core event configuration used across the app.
 * Wraps /api/event and /api/admin/event-config PUT.
 */
export default function EventSettings() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "tl_internal")) { nav("/login"); return; }
    api.get("/event").then(({ data }) => setCfg(data)).catch(() => setCfg({})).finally(() => setLoadingCfg(false));
  }, [user, loading, nav]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        name: cfg.name || "",
        date: cfg.date || "",
        location: cfg.location || "",
        address: cfg.address || "",
        registration_status: cfg.registration_status || "open",
        supplier_package_price: cfg.supplier_package_price != null ? Number(cfg.supplier_package_price) : null,
        max_suppliers: cfg.max_suppliers != null ? Number(cfg.max_suppliers) : null,
        support_email: cfg.support_email || "",
        parking_instructions: cfg.parking_instructions || "",
        dress_code: cfg.dress_code || "",
        wifi: cfg.wifi || "",
        emergency_contact: cfg.emergency_contact || "",
      };
      // Strip null/empty to keep the PUT clean (backend uses exclude_unset + None filter)
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null || Number.isNaN(payload[k])) delete payload[k];
      });
      const { data } = await api.put("/admin/event-config", payload);
      setCfg((c) => ({ ...c, ...data.event_config }));
      toast.success("Event settings saved.");
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : "Save failed.");
    } finally { setSaving(false); }
  };

  if (loading || loadingCfg) return <Layout><section className="container-summit py-24"><div className="overline">Loading…</div></section></Layout>;
  if (!cfg) return null;

  return (
    <Layout>
      <section className="border-b border-[hsl(var(--line))] bg-[#0A0A0A] text-white">
        <div className="container-summit py-10 md:py-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Link to="/command-center" className="text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-white inline-flex items-center gap-2">
              <ArrowLeft size={12} /> Back to Command Center
            </Link>
            <div className="overline mt-6 text-white/60"><span className="accent-rule" />Event Settings</div>
            <h1 className="serif text-4xl sm:text-5xl mt-3">Configure the Summit.</h1>
            <p className="mt-3 text-sm text-white/70 max-w-2xl">
              Single source of truth for event details, registration policy, and day-of logistics used across every page and email.
            </p>
          </div>
          <button onClick={save} disabled={saving} className="btn-black h-11 px-6" data-testid="es-save">
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </section>

      <section className="container-summit py-10 md:py-14 grid gap-10 max-w-4xl">
        <Section title="Core Event">
          <Grid>
            <Field label="Event Name">
              <Input value={cfg.name || ""} onChange={(e) => set("name", e.target.value)}
                data-testid="es-name" className="h-11 rounded-none" />
            </Field>
            <Field label="Date">
              <Input value={cfg.date || ""} onChange={(e) => set("date", e.target.value)}
                placeholder="Friday, September 11, 2026"
                data-testid="es-date" className="h-11 rounded-none" />
            </Field>
            <Field label="Location">
              <Input value={cfg.location || ""} onChange={(e) => set("location", e.target.value)}
                data-testid="es-location" className="h-11 rounded-none" />
            </Field>
            <Field label="Address">
              <Input value={cfg.address || ""} onChange={(e) => set("address", e.target.value)}
                data-testid="es-address" className="h-11 rounded-none" />
            </Field>
          </Grid>
        </Section>

        <Section title="Registration Policy">
          <Grid>
            <Field label="Registration Status">
              <Select value={cfg.registration_status || "open"} onValueChange={(v) => set("registration_status", v)}>
                <SelectTrigger className="h-11 rounded-none" data-testid="es-registration-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="waitlist">Waitlist</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Supplier Package Price (USD)">
              <Input type="number" min="0" step="1" value={cfg.supplier_package_price ?? ""}
                onChange={(e) => set("supplier_package_price", e.target.value ? Number(e.target.value) : null)}
                placeholder="2500"
                data-testid="es-price" className="h-11 rounded-none" />
            </Field>
            <Field label="Maximum Suppliers">
              <Input type="number" min="1" step="1" value={cfg.max_suppliers ?? ""}
                onChange={(e) => set("max_suppliers", e.target.value ? Number(e.target.value) : null)}
                placeholder="25"
                data-testid="es-max-suppliers" className="h-11 rounded-none" />
            </Field>
            <Field label="Support Email">
              <Input type="email" value={cfg.support_email || ""}
                onChange={(e) => set("support_email", e.target.value)}
                placeholder="info@displayamerica.com"
                data-testid="es-support-email" className="h-11 rounded-none" />
            </Field>
          </Grid>
        </Section>

        <Section title="Attendee Logistics">
          <div className="grid gap-5">
            <Field label="Parking Instructions">
              <Textarea rows={3} value={cfg.parking_instructions || ""}
                onChange={(e) => set("parking_instructions", e.target.value)}
                placeholder="e.g. Complimentary parking is available in the north garage; validation at the registration desk."
                data-testid="es-parking" className="rounded-none" />
            </Field>
            <Grid>
              <Field label="Dress Code">
                <Input value={cfg.dress_code || ""} onChange={(e) => set("dress_code", e.target.value)}
                  placeholder="Business professional"
                  data-testid="es-dress-code" className="h-11 rounded-none" />
              </Field>
              <Field label="Wi-Fi">
                <Input value={cfg.wifi || ""} onChange={(e) => set("wifi", e.target.value)}
                  placeholder="Network · Password"
                  data-testid="es-wifi" className="h-11 rounded-none" />
              </Field>
            </Grid>
            <Field label="Emergency Contact">
              <Input value={cfg.emergency_contact || ""} onChange={(e) => set("emergency_contact", e.target.value)}
                placeholder="On-site coordinator name & email"
                data-testid="es-emergency" className="h-11 rounded-none" />
            </Field>
          </div>
        </Section>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="btn-black h-11 px-6" data-testid="es-save-bottom">
            <Save size={14} /> {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </section>
    </Layout>
  );
}

function Section({ title, children }) {
  return (
    <div className="grid gap-5" data-testid={`es-section-${title.toLowerCase().replace(/\s/g,"-")}`}>
      <div className="overline"><span className="accent-rule" />{title}</div>
      {children}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid sm:grid-cols-2 gap-5">{children}</div>;
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="overline">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
